import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch } from "@fortawesome/free-solid-svg-icons";
import axios from "axios";
import Grid from "../components/Grid"; // Import the Grid component
import { FaEdit } from "react-icons/fa";
import { toast } from "react-toastify";
import { FaDownload } from "react-icons/fa";
import { saveAs } from "file-saver";
import ExcelJS from "exceljs";


// Use the environment variable for the API base URL
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function Users({ onAddUserClick }) {
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [currentUser, setCurrentUser] = useState(null); 
  const [searchTerm, setSearchTerm] = useState("");
  const [locations, setLocations] = useState([[]]);
  const [selectedSearchField, setSelectedSearchField] = useState("username");

  useEffect(() => {
    // Fetch current user data
    const fetchCurrentUser = async () => {
      try {
        const token = sessionStorage.getItem("token");
        if (!token) {
          toast.error("Authentication token is missing. Please log in.");
          return;
        }

        const response = await axios.get(`${API_BASE_URL}/users/specific`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        setCurrentUser(response.data);

      } catch (error) {
        console.error("Error fetching current user data:", error);
        toast.error("Failed to fetch current user data.");
      }
    };

    fetchCurrentUser();
  }, []);

  useEffect(() => {

    const fetchLocations = async () => {
      try {
        const token = sessionStorage.getItem("token");
        if (!token) {
          toast.error("Authentication token is missing. Please log in again.");
          return;
        }

        const response = await axios.get(`${API_BASE_URL}/locations`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const locationsWithId = response.data.locations.map((location) => ({
          ...location,
          id: location.location_code, // Map location_code to id
        }));

        setLocations(locationsWithId);
      } catch (error) {
        console.error("Error fetching locations:", error);
        toast.error("Error fetching locations. Please try again.");
      }
    };

    // Fetch users only if currentUser is set and has valid data
    const fetchUsers = async () => {
      if (!currentUser) return; // Ensure currentUser is loaded before proceeding

      try {
        const token = sessionStorage.getItem("token");
        if (!token) {
          toast.error("Authentication token is missing. Please log in.");
          return;
        }

        // Modify the API call based on the current user's role
        let apiUrl = `${API_BASE_URL}/users`; // Default for Main Admin
        if (currentUser.role === "Company Admin" && currentUser.organization) {
          // Filter users by organization for Company Admin
          apiUrl = `${API_BASE_URL}/users?organization=${currentUser.organization._id}`;
        }

        const response = await axios.get(apiUrl, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        // Ensure response.data exists before accessing users
        if (response.data && response.data.users) {
          const usersWithId = response.data.users.map((user) => ({
            ...user,
            id: user.user_code,
          }));
          setUsers(usersWithId);
        } else {
          console.error("Unexpected response structure:", response);
        }
      } catch (error) {
        console.error(
          "Error fetching users:",
          error.response ? error.response.data.message : error.message
        );
        toast.error("Failed to fetch users. Please try again.");
      }
    };

    if (currentUser?.role === "Company Admin") {
      fetchLocations();
    }

    fetchUsers();
  }, [currentUser]); // Fetch users again when currentUser changes

  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const token = sessionStorage.getItem("token");
        if (!token) {
          toast.error("Authentication token is missing. Please log in again.");
          return;
        }
  
        const response = await axios.get(`${API_BASE_URL}/locations`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
  
        const locationsWithId = response.data.locations.map((location) => ({
          ...location,
          id: location.location_code, // Map location_code to id
        }));
  
        setLocations(locationsWithId);
      } catch (error) {
        console.error("Error fetching locations:", error);
        toast.error("Error fetching locations. Please try again.");
      }
    };
  
    if (currentUser?.role === "Company Admin") {
      fetchLocations();
    }
  }, [currentUser]); 
  

  const handleUserClick = (user) => {
    setSelectedUser({
      ...user,
      username: user.username || "",
      user_email: user.user_email || "",
      password: user.password || "",
      role: user.role || "",
      location: user.location ,
      status: user.status || "Active",
    });
  };

  const handleClosePopup = () => {
    setSelectedUser(null);
  };

  const columns = [
    { field: "user_code", headerName: "ID", flex: 1 },
    { field: "username", headerName: "User Name", flex: 1 },
    { field: "user_email", headerName: "User Email", flex: 1 },
    { field: "role", headerName: "User Type", flex: 1 },
    {
      field: "location",
      headerName: "Locations",
      flex: 1,
      renderCell: (params) => {
        const locations = params.row.location;
        return (
          <span>{locations?.map(loc => loc.location_name).join(", ") || "No Location"}</span>
        );
      },
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      renderCell: (params) => {
        const status = params.row.status;
        const statusClass =
          status === "Active"
            ? "bg-[#388E3C] text-white"
            : "bg-[#E57373] text-white";

        return (
          <span className={`px-2 py-1 rounded-sm ${statusClass}`}>
            {status}
          </span>
        );
      },
    },
    {
      field: "action",
      headerName: "Action",
      flex: 1,
      renderCell: (params) => (
        <div className="flex justify-left items-left w-10 h-10">
          <button
            onClick={() => handleUserClick(params.row)}
            className="bg-[#28ad4b] text-white px-2 py-1 flex items-center rounded-lg"
          >
            <FaEdit className="text-white mr-2" />
            Edit
          </button>
        </div>
      ),
    },
  ];
  const filteredUsers = users.filter((user) => {
    const search = searchTerm.toLowerCase();

    if (!search) return true; // If no search term, show all users

    switch (selectedSearchField) {
      case "username":
        return user.username?.toLowerCase().includes(search);
      case "user_id":
      case "user_code":
        return user.user_code?.toLowerCase().includes(search);
      case "user_email":
        return user.user_email?.toLowerCase().includes(search);
      case "role":
        return user.role?.toLowerCase().includes(search);
      case "status":
        return user.status?.toLowerCase().includes(search);
      case "location":
        return (
          Array.isArray(user.location) &&
          user.location.some((loc) =>
            loc.location_name?.toLowerCase().includes(search)
          )
        );
      default:
        return false;
    }
  });

  const handleDownloadExcel = async () => {
  const filteredUsers = users.filter((user) => {
    const value = user[selectedSearchField];

    if (Array.isArray(value)) {
      return value.some((item) =>
        item.toString().toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    if (typeof value === "object" && value !== null) {
      return Object.values(value).some((val) =>
        val.toString().toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    return value?.toString().toLowerCase().includes(searchTerm.toLowerCase());
  });

  const dataToExport = searchTerm.trim().length > 0 ? filteredUsers : users;

  if (dataToExport.length === 0) {
    toast.warn("No user data to export.");
    return;
  }

  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet("Users");

  const headerRow = [
    "User ID",
    "User Name",
    "User Email",
    "User Type",
    "Locations",
    "Status",
  ];
  worksheet.addRow(headerRow);

  const header = worksheet.getRow(1);
  header.eachCell((cell) => {
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFD966" }, // Light yellow
    };
    cell.font = { bold: true };
    cell.border = {
      top: { style: "thin" },
      left: { style: "thin" },
      bottom: { style: "thin" },
      right: { style: "thin" },
    };
  });

  dataToExport.forEach((user) => {
    worksheet.addRow([
      user.user_code,
      user.username,
      user.user_email,
      user.role,
      Array.isArray(user.location)
        ? user.location.map((loc) => loc.location_name).join(", ")
        : "No Location",
      user.status,
    ]);
  });

  worksheet.columns.forEach((column) => {
    column.width = column.header
      ? column.header.toString().length < 20
        ? 20
        : column.header.toString().length
      : 20;
  });

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  saveAs(blob, "users.xlsx");
};

  const handleUpdateUser = async () => {
    try {
      const token = sessionStorage.getItem("token");

        if (!token) {
          toast.error("Authentication token is missing. Please log in again.");
          return;
        }
      await axios.put(
        `${API_BASE_URL}/users/update/${selectedUser._id}`,
        {
          username: selectedUser.username,
          newUserEmail: selectedUser.user_email,
          newRole: selectedUser.role,  // Make sure this is correctly mapped
          newLocation: selectedUser.location,
          newOrganization: selectedUser.organization,
          profile_image: selectedUser.profile_image,
          status: selectedUser.status,
        },
        {
          headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          },
        }   
      );
      toast.success("User Updated Successfully");
      setSelectedUser(null);
      // Refresh the User list
      const response = await axios.get(`${API_BASE_URL}/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setUsers(response.data.users);
    } catch (error) {
      console.error("Error updating user:", error);
      if (error.response) {
        const errorMessage = error.response.data.message || "Error Updating User";
        toast.error(errorMessage);
      } else if (error.request) {
        toast.error("No response from the server. Please try again.");
      } else {
        toast.error("An unexpected error occurred.");
      }
    }
  };

  return (
    <div className="flex h-screen">
      {/* Main Content */}
      <div className="flex-1 p-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-green-700 ">Users</h1>
          <button
            onClick={onAddUserClick}
            className="bg-green-700 text-white px-4 py-2 rounded-lg"
          >
            Add User
          </button>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-md">
          <div className="flex justify-between mb-4 items-center">

            {/* Left Section: Search filters */}
            <div className="flex items-center flex-wrap gap-4">
              <select
                className="px-4 py-2 border border-gray-300 rounded-lg"
                value={selectedSearchField}
                onChange={(e) => setSelectedSearchField(e.target.value)}
              >
                <option value="username">User Name</option>
                <option value="user_id">User ID</option>
                <option value="user_email">User Email</option>
                <option value="role">User Type</option>
                <option value="status">Status</option>
                <option value="location">Location</option>
              </select>

              <div className="relative">
                <input
                  type="text"
                  placeholder="Search users here"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="px-4 py-2 pl-10 border border-gray-300 rounded-lg w-full max-w-xs"
                />
                <div className="absolute inset-y-0 left-0 flex items-center pl-3">
                  <FontAwesomeIcon icon={faSearch} className="text-gray-500" />
                </div>
              </div>
            </div>

            {/* Right Section: Download Excel */}
            <div>
              <button
                onClick={handleDownloadExcel}
                className="flex items-center bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
              >
                <FaDownload className="mr-2" />
                Download Excel
              </button>
            </div>
          </div>

          {/* user Grid */}
        <Grid rows={filteredUsers} columns={columns} getRowId={(row) => row.id || row._id} />
        </div>

        {/* Popup for User Details */}
        {selectedUser && (
          <div className="fixed inset-0 flex items-center justify-center bg-gray-900 bg-opacity-50 z-50">
            <div className="bg-white p-8 rounded-lg shadow-lg w-full md:w-2/3 lg:w-1/2 relative">
              <button
                onClick={handleClosePopup}
                className="absolute top-2 right-2 bg-red-500 text-white w-8 h-8 flex justify-center items-center rounded-sm hover:bg-red-700"
              >
                &times;
              </button>
              <div className="flex mb-4">
                <div className="w-1/2 pl-4">
                  <h2 className="text-xl font-semibold mb-4">
                    {selectedUser.user_code}
                  </h2>
                  {/* user name */}
                  <div className="mb-4">
                    <label className="block text-gray-700">User Name</label>
                    <input
                      type="text"
                      value={selectedUser.username}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                      onChange={(e) =>
                        setSelectedUser({
                          ...selectedUser,
                          username: e.target.value,
                        })
                      }
                    />
                  </div>
                  {/* user email */}
                  <div className="mb-4">
                    <label className="block text-gray-700">
                      User Email Address
                    </label>
                    <input
                      type="text"
                      value={selectedUser.user_email}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                      onChange={(e) =>
                        setSelectedUser({
                          ...selectedUser,
                          user_email: e.target.value,
                        })
                      }
                    />
                  </div>
                  {/* user Type */}
                  <div className="mb-4">
                    <label className="block text-gray-700">User Type</label>
                    <select
                      value={selectedUser.role}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                      onChange={(e) =>
                        setSelectedUser({
                          ...selectedUser,
                          role: e.target.value,
                        })
                      }
                    >
                      <option value="" disabled>
                        Select User Role
                      </option>
                      {currentUser?.role === "Company Admin" && (
                        <>
                          <option value="Farm User">Farm User</option>
                          <option value="Outlet User">Outlet User</option>
                          <option value="Accountant">Accountant</option>
                        </>
                      )}
                      {currentUser?.role === "Main Admin" && (
                        <option value="Company Admin">Company Admin</option>
                      )}
                    </select>
                    </div>
                    {/* Locations Multi-Select */}
                    <div className="mb-4">
                      <label className="block text-gray-700">User Locations</label>
                      <div className="grid grid-cols-2 gap-4">
                        {locations.map((loc) => {
                          // Ensure selectedUser.location is an array
                          const isChecked = selectedUser.location?.some((l) => l._id === loc._id) || false;
                          return (
                            <div key={loc._id} className="flex items-center">
                              <input
                                type="checkbox"
                                id={`location-${loc._id}`}
                                checked={isChecked}
                                onChange={(e) => {
                                  const isChecked = e.target.checked;
                                  setSelectedUser((prev) => {
                                    let updatedLocations = prev.location || [];

                                    if (isChecked) {
                                      updatedLocations = [...updatedLocations, { _id: loc._id, location_name: loc.location_name }];
                                    } else {
                                      updatedLocations = updatedLocations.filter((l) => l._id !== loc._id);
                                    }

                                    return { ...prev, location: updatedLocations };
                                  });
                                }}
                              />
                              <label htmlFor={`location-${loc._id}`} className="ml-2">
                                {loc.location_name}
                              </label>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    {/* Status Dropdown */}
                    <div className="mb-4">
                      <label className="block text-gray-700">Status</label>
                      <select
                        value={selectedUser.status}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                        onChange={(e) =>
                          setSelectedUser({
                            ...selectedUser,
                            status: e.target.value,
                          })
                        }
                      >
                        <option value="">Select Status</option>
                        <option value="Active">Active</option>
                        <option value="Inactive">Inactive</option>
                      </select>
                    </div>
                  {/* Buttons */}
                  <div className="flex justify-end">
                    <button
                      onClick={handleUpdateUser}
                      className="bg-blue-500 text-white px-4 py-2 rounded-lg"
                    >
                      Update
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Users;
